"use client"

import { useVisualizer, VisualizerProvider } from "@/lib/visualizer-context"
import { FormPreview } from "@/components/form-preview"
import { Button } from "@/components/ui/button"
import { useState, useEffect } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Menu } from "lucide-react"
import { PDFViewer } from "@/components/PdfViewer"
import { getCSRFToken } from "@/lib/utils"
import toast, { Toaster } from "react-hot-toast"
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Fax } from "@/types"
import { createPdfExtractionChannel } from "@/channels/pdf_extraction_channel"
import { tracker } from "@openreplay/tracker"

function DataEditorPage() {

  const { props: { user  } } = usePage()
  tracker.setUserID(user?.email);
  console.log("Rendering DataEditorPage")
  const { config, pdf_id } = usePage().props
  const fax: Fax = usePage().props.fax as Fax
  console.log("FAX in DataEditorPage:", fax)

  const { data, visualizerConfig, dataSchema } = useVisualizer()

  const [localData, setLocalData] = useState(data)
  const [dataChanged, setDataChanged] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Track changes
  useEffect(() => {
    const isEqual = JSON.stringify(localData) === JSON.stringify(data)
    setDataChanged(!isEqual)
  }, [localData, data])

  const { toast: toastMessage } = usePage().props
  useEffect(() => {
    if (toastMessage !== null && toastMessage === "data updated") {
      toast("Data updated successfully")
    }
  }, [])




  useEffect(() => {
    const wss = createPdfExtractionChannel(["users"], fax)

    return () => {
      wss.unsubscribe()
    }
  }, [])

  // SAVE
  const handleSave = () => {
    fetch(`/pdf_extraction/${pdf_id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken() || "",
      },
      body: JSON.stringify({ id: pdf_id, final_result: localData }),
    })
  }

  // RESET
  const handleReset = () => {
    setLocalData(data)
  }

  const capitalize = (s: string) => {
    if (typeof s !== "string") return ""
    return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1)
  }

  return (
    <div className="h-screen flex flex-col bg-background disable-scroll">
      {/* HEADER */}
      <Toaster />
      <header className="border-b border-border bg-muted/30 px-4 py-4">
        <div className="flex items-center justify-between  mx-auto">
          <Link href="/">
            <h1 className="text-2xl md:text-3xl font-bold">
              {capitalize(config?.name) || "Fax"}
            </h1>
          </Link>

          {/* DESKTOP ACTION BUTTONS */}
          <div className="hidden md:flex gap-2">
            <Button variant="outline" onClick={handleReset}>
              Reset Changes
            </Button>

            <Button
              variant={dataChanged ? "destructive" : "ghost"}
              disabled={!dataChanged}
              onClick={handleSave}
            >
              Save Data
            </Button>
          </div>

          {/* MOBILE MENU */}
          <div className="md:hidden flex gap-2">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right">
                <SheetHeader>
                  <SheetTitle>Actions</SheetTitle>
                </SheetHeader>

                <div className="flex flex-col mt-6 gap-3">
                  <Button variant="outline" onClick={handleReset}>
                    Reset Changes
                  </Button>

                  <Button
                    variant={dataChanged ? "destructive" : "ghost"}
                    disabled={!dataChanged}
                    onClick={handleSave}
                  >
                    Save Data
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* TABS (PDF | Extraction) */}
      <Tabs defaultValue="pdf" className="flex-1 flex flex-col">
        <TabsList className="border-b bg-background px-4 py-2">
          <TabsTrigger value="pdf">PDF</TabsTrigger>
          <TabsTrigger value="extraction">Extraction</TabsTrigger>
        </TabsList>

        {/* ---- PDF TAB ---- */}
        {console.log("Rendering PDFViewer with fax.id:", fax.id)}
        <TabsContent value="pdf" className="flex-1 overflow-hidden">
          <PDFViewer pdfUrl={`${fax.id}`} fileName="record.pdf" />
        </TabsContent>

        {/* ---- EXTRACTION TAB ---- */}
        {
          visualizerConfig && dataSchema && (
            <TabsContent value="extraction" className="flex-1  p-4">
              <FormPreview
                config={visualizerConfig}
                dataSchema={dataSchema}
                data={localData}
                editable={true}
                onDataChange={setLocalData}
              />
            </TabsContent>
          )
        }

      </Tabs>
    </div>
  )
}

DataEditorPage.layout = (page: any) => (
  <VisualizerProvider config={page?.props.config} data={page?.props.data}>
    {page}
  </VisualizerProvider>
)

export default DataEditorPage


